import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./auth";
import {
    ServerToClientEvents as S2C,
    ClientToServerEvents as C2S,
} from "../types/socket-events";

interface SocketCtx {
    socket: Socket<S2C, C2S> | null;
    connected: boolean;
    isDisabled: boolean;
}

const SocketContext = createContext<SocketCtx | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { token, user } = useAuth();
    const [connected, setConnected] = useState(false);
    const socketRef = useRef<Socket<S2C, C2S> | null>(null);
    const isDisabled = process.env.ENABLE_SOCKET === "false";

    useEffect(() => {
        // Check if socket is disabled via environment variable
        if (isDisabled) {
            console.info("Socket connection disabled via environment variable");
            return;
        }

        if (!token || !user) {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                setConnected(false);
            }
            return;
        }

        const socket = io(process.env.REACT_APP_WS_API_URL!, {
            transports: ["websocket"],
            autoConnect: false,
            auth: { token },
            path: "/socket.io",
        });

        socket.on("connect", () => setConnected(true));
        socket.on("disconnect", () => setConnected(false));
        socket.on("connect_error", (err) => {
            console.warn("WS error:", err.message);
            if (err.message === "JWT token expired") {
                console.warn("Token expired, disconnecting socket");
                socket.disconnect();
                socketRef.current = null;
                setConnected(false);
            }
        });

        socketRef.current = socket;
        socket.connect();

        return () => {
            socket.disconnect();
            socketRef.current = null;
            setConnected(false);
        };
    }, [token, user, isDisabled]);

    return (
        <SocketContext.Provider
            value={{ socket: socketRef.current, connected, isDisabled }}
        >
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = (strict: boolean = true) => {
    const ctx = useContext(SocketContext);
    if (!ctx) throw new Error("useSocket must be inside <SocketProvider>");

    // If socket is disabled, return null without throwing error
    if (ctx.isDisabled) {
        return null;
    }

    if ((!ctx.socket || !ctx.connected) && strict) {
        throw new Error("Socket not connected");
    }
    return ctx.socket;
};